// Authentic Multi-Board Ludo Party Battle (2P, 3P, 4P, 5P, 6P)
// Featuring:
// - Exact 5P & 6P Route & Geometry as Requested:
//   - 3x6 Line paths between player initial resting places (Lobbies)
//   - Player tokens exit from right side of their lobby onto 1st line, 2nd square from outer end (Row 4)
//   - That initial entry square is a SAFE STAR POINT (★) with player's color
//   - 4 steps inward along 1st line to Row 0 (innermost square)
//   - 5th step takes a natural LEFT TURN into 1st row (Row 0) of neighbouring 3x6 columns (not jumping!)
//   - Travels outward up that column: at step 8 is the SAFE STAR POINT (☆)
//   - From that safe star point, 5 steps around tip to the next player's initial starting square!
//   - Middle Line 2 highlights 5 squares from home to outside in player's respective color
//   - 6th square at outer end has inward arrow pointing towards home
//   - DEDICATED COLOR HOME SLOTS: All home reached tokens sit on their dedicated team color in center
// - Multi-Player Finish & Leaderboard (No stopping in middle!):
//   - Game continues until all N-1 players complete!
//   - In-game text banners for player finishes (1st, 2nd, 3rd, etc.) - NO alert() popups!
//   - "Leave Match" option for players who want to exit
//   - Final Leaderboard modal with rankings, kills, deaths, tokens home, and Return to Lobby/Hub buttons
// - Classic 15x15 Board for 2P, 3P, 4P preserved intact

import { sounds } from '../sound.js';
import { LUDO_BOARDS } from '../../server/gameEngines/ludoEngine.js';

export class LudoGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.gameState = 'LOBBY'; // 'LOBBY' | 'IN_GAME'
    this.matchSize = '4P';
    this.boardConfig = LUDO_BOARDS['4P'];
    this.players = {};
    this.currentTurnId = null;
    this.currentDice = 1;
    this.movableTokens = [];
    this.pendingMovableTokens = [];

    // Pre-game Customizations
    this.selectedColor = 'red';
    this.selectedPawnStyle = 'toy';
    this.customizations = {}; // playerId -> { color, pawnStyle }
    this.roomPlayers = [];
    this.hostId = null;

    // Animation & Hopping state
    this.isRolling = false;
    this.activeHops = [];
    this.animFrameId = null;
    this.bannerMessage = null;
    this.bannerTimer = null;

    this.colorHexes = {
      red: '#ef4444',
      orange: '#f97316',
      yellow: '#eab308',
      green: '#16a34a',
      blue: '#2563eb',
      purple: '#7c3aed',
      pink: '#db2777',
      olive: '#65a30d'
    };

    this.pawnStyles = [
      { id: 'toy', name: '🧸 Classic Toy', desc: 'Glossy wooden toy pawn' },
      { id: 'crown', name: '👑 Royal Crown', desc: 'Regal crown with gold jewels' },
      { id: 'rocket', name: '🚀 Cyber Rocket', desc: 'Futuristic rocket with thruster' },
      { id: 'gem', name: '💎 Crystal Gem', desc: 'Faceted prismatic diamond' },
      { id: 'chibi', name: '🐱 Chibi Mascot', desc: 'Cute anime animal with paws' }
    ];

    this.initGeometries();
    this.render();
    this.setupSocketEvents();
    this.startAnimationLoop();
  }

  // ========================================================
  // 1. GEOMETRY BUILDERS
  // ========================================================
  initGeometries() {
    // ----------------------------------------------------
    // Classic 15x15 Grid (Preserved intact for 2P, 3P, 4P)
    // ----------------------------------------------------
    this.coords15 = {
      gridSize: 15,
      cellSize: 40,
      path: [
        { c: 1, r: 6 }, { c: 2, r: 6 }, { c: 3, r: 6 }, { c: 4, r: 6 }, { c: 5, r: 6 },
        { c: 6, r: 5 }, { c: 6, r: 4 }, { c: 6, r: 3 }, { c: 6, r: 2 }, { c: 6, r: 1 }, { c: 6, r: 0 },
        { c: 7, r: 0 }, { c: 8, r: 0 },
        { c: 8, r: 1 }, { c: 8, r: 2 }, { c: 8, r: 3 }, { c: 8, r: 4 }, { c: 8, r: 5 },
        { c: 9, r: 6 }, { c: 10, r: 6 }, { c: 11, r: 6 }, { c: 12, r: 6 }, { c: 13, r: 6 }, { c: 14, r: 6 },
        { c: 14, r: 7 }, { c: 14, r: 8 },
        { c: 13, r: 8 }, { c: 12, r: 8 }, { c: 11, r: 8 }, { c: 10, r: 8 }, { c: 9, r: 8 },
        { c: 8, r: 9 }, { c: 8, r: 10 }, { c: 8, r: 11 }, { c: 8, r: 12 }, { c: 8, r: 13 }, { c: 8, r: 14 },
        { c: 7, r: 14 }, { c: 6, r: 14 },
        { c: 6, r: 13 }, { c: 6, r: 12 }, { c: 6, r: 11 }, { c: 6, r: 10 }, { c: 6, r: 9 },
        { c: 5, r: 8 }, { c: 4, r: 8 }, { c: 3, r: 8 }, { c: 2, r: 8 }, { c: 1, r: 8 }, { c: 0, r: 8 },
        { c: 0, r: 7 }, { c: 0, r: 6 }
      ],
      homeStretches: {
        red: [{ c: 1, r: 7 }, { c: 2, r: 7 }, { c: 3, r: 7 }, { c: 4, r: 7 }, { c: 5, r: 7 }],
        green: [{ c: 7, r: 1 }, { c: 7, r: 2 }, { c: 7, r: 3 }, { c: 7, r: 4 }, { c: 7, r: 5 }],
        yellow: [{ c: 13, r: 7 }, { c: 12, r: 7 }, { c: 11, r: 7 }, { c: 10, r: 7 }, { c: 9, r: 7 }],
        blue: [{ c: 7, r: 13 }, { c: 7, r: 12 }, { c: 7, r: 11 }, { c: 7, r: 10 }, { c: 7, r: 9 }]
      },
      bases: {
        red: [{ c: 1.8, r: 1.8 }, { c: 3.8, r: 1.8 }, { c: 1.8, r: 3.8 }, { c: 3.8, r: 3.8 }],
        green: [{ c: 10.8, r: 1.8 }, { c: 12.8, r: 1.8 }, { c: 10.8, r: 3.8 }, { c: 12.8, r: 3.8 }],
        yellow: [{ c: 10.8, r: 10.8 }, { c: 12.8, r: 10.8 }, { c: 10.8, r: 12.8 }, { c: 12.8, r: 12.8 }],
        blue: [{ c: 1.8, r: 10.8 }, { c: 3.8, r: 10.8 }, { c: 1.8, r: 12.8 }, { c: 3.8, r: 12.8 }]
      },
      homeCenter: { x: 300, y: 300 }
    };

    // ----------------------------------------------------
    // Radial Geometries (6P, 5P) - Exact Rules & Math!
    // ----------------------------------------------------
    this.coords6P = this.buildRadialGeom(6, LUDO_BOARDS['6P'].colors);
    this.coords5P = this.buildRadialGeom(5, LUDO_BOARDS['5P'].colors);
  }

  static getArmPoint(armAngle, lateral, radial, cx = 300, cy = 300) {
    const Rx = Math.cos(armAngle);
    const Ry = Math.sin(armAngle);
    const Lx = -Math.sin(armAngle);
    const Ly = Math.cos(armAngle);
    return {
      x: cx + Rx * radial + Lx * lateral,
      y: cy + Ry * radial + Ly * lateral
    };
  }

  // ========================================================
  // 5P & 6P ROUTE PATH WITH EXACT 13-STEP SECTOR GEOMETRY:
  // - START square at Row 4 of Line 1 (2nd from outer end, safe star ★)
  // - 4 steps inward along Line 1 to Row 0
  // - 5th step takes a natural LEFT TURN into Row 0 of neighbouring arm (not jumping!)
  // - Travels outward up Line 3: Step 8 is the ONLY safe star point (☆)
  // - 5 steps from Step 8 to next player's initial starting square
  // - Middle Line 2 highlights 5 squares from home to outside in respective color
  // - DEDICATED COLOR HOME SLOTS: All reached tokens sit on their team color
  // ========================================================
  buildRadialGeom(armsCount, colors) {
    const cx = 300, cy = 300;
    const path = [];
    const homeStretches = {};
    const bases = {};
    const dedicatedHomeSlots = {};
    const stepAngle = (2 * Math.PI) / armsCount;
    const startAngleOffset = -Math.PI / 2; // Arm 0 points Up (12 o'clock, Red-Green)

    const getArmPt = (armIndex, lateral, radial) => {
      const armAngle = startAngleOffset + armIndex * stepAngle;
      return LudoGameClient.getArmPoint(armAngle, lateral, radial, cx, cy);
    };

    colors.forEach((color, pIdx) => {
      // Sector pIdx (13 steps):
      // Starts at Player pIdx's start square on Arm (pIdx - 1)
      const prevArm = (pIdx - 1 + armsCount) % armsCount;
      const curArm = pIdx;

      // Step 0: Player pIdx's Initial Entry on Line 3 (touching Player pIdx's lobby) at Row 4
      path.push(getArmPt(prevArm, 22, 200));

      // Steps 1-4: Travel inward down Line 3 of prevArm towards center (Rows 3, 2, 1, 0)
      path.push(getArmPt(prevArm, 22, 168)); // Step 1: Row 3
      path.push(getArmPt(prevArm, 22, 136)); // Step 2: Row 2
      path.push(getArmPt(prevArm, 22, 104)); // Step 3: Row 1
      path.push(getArmPt(prevArm, 22, 72));  // Step 4: Row 0 (innermost square near center)

      // Step 5: Turns at center into Arm curArm (between Player pIdx and Player pIdx + 1)!
      // Enters Line 1 (the line next to Player pIdx's lobby) at Row 0!
      path.push(getArmPt(curArm, -22, 72)); // Step 5: Row 0 of Line 1

      // Steps 6-10: Travel outward along Line 1 towards outer tip (Rows 1, 2, 3, 4, 5)
      path.push(getArmPt(curArm, -22, 104)); // Step 6: Row 1
      path.push(getArmPt(curArm, -22, 136)); // Step 7: Row 2
      path.push(getArmPt(curArm, -22, 168)); // Step 8: Row 3 (SAFE STAR POINT! Exactly 8 steps from Step 0!)
      path.push(getArmPt(curArm, -22, 200)); // Step 9: Row 4
      path.push(getArmPt(curArm, -22, 232)); // Step 10: Row 5 (outer tip)

      // Steps 11-12: Tip turnaround into Line 3 of curArm:
      path.push(getArmPt(curArm, 0, 232));  // Step 11: Tip middle
      path.push(getArmPt(curArm, 22, 232)); // Step 12: Tip of Line 3
      // (Step 12 connects directly to Step 0 of next player at getArmPt(curArm, 22, 200), exactly 5 steps from Step 8!)

      // Home Runway: 5 colored squares along Middle Line 2 of Arm prevArm (e.g. Red between Purple & Red)
      homeStretches[color] = [
        getArmPt(prevArm, 0, 200), // Row 4 (5th square from home)
        getArmPt(prevArm, 0, 168), // Row 3
        getArmPt(prevArm, 0, 136), // Row 2
        getArmPt(prevArm, 0, 104), // Row 1
        getArmPt(prevArm, 0, 72)   // Row 0 (1st square from home)
      ];

      // Base (Lobby): In wedge between Arm prevArm and Arm curArm
      const curArmAngle = startAngleOffset + curArm * stepAngle;
      const baseAngle = curArmAngle - stepAngle / 2;
      const bx = cx + Math.cos(baseAngle) * 206;
      const by = cy + Math.sin(baseAngle) * 206;
      const d = 16;
      bases[color] = [
        { x: bx - d, y: by - d },
        { x: bx + d, y: by - d },
        { x: bx - d, y: by + d },
        { x: bx + d, y: by + d }
      ];

      // Dedicated Home Slots: 4 distinct spots inside that color's center triangle at base of prevArm
      const prevArmAngle = startAngleOffset + prevArm * stepAngle;
      dedicatedHomeSlots[color] = [
        LudoGameClient.getArmPoint(prevArmAngle, -11, 46, cx, cy), // Slot 0
        LudoGameClient.getArmPoint(prevArmAngle, 11, 46, cx, cy),  // Slot 1
        LudoGameClient.getArmPoint(prevArmAngle, -7, 24, cx, cy),  // Slot 2
        LudoGameClient.getArmPoint(prevArmAngle, 7, 24, cx, cy)    // Slot 3
      ];
    });

    return {
      armsCount,
      colors,
      path,
      homeStretches,
      bases,
      dedicatedHomeSlots,
      homeCenter: { x: cx, y: cy }
    };
  }

  // ========================================================
  // 2. MAIN RENDER ROUTER
  // ========================================================
  render() {
    if (this.gameState === 'LOBBY') {
      this.renderSetupScreen();
    } else {
      this.renderGameScreen();
    }
  }

  // ========================================================
  // 3. PRE-GAME MATCH SETUP SCREEN
  // ========================================================
  renderSetupScreen() {
    const isHost = this.socket.id === this.hostId || this.roomPlayers.length <= 1;
    const currentBoard = LUDO_BOARDS[this.matchSize] || LUDO_BOARDS['4P'];

    this.container.innerHTML = `
      <div class="ludo-setup-screen">
        <div class="ludo-setup-card">
          <div class="ludo-setup-title">
            <h2>🎲 LUDO MATCH SETUP</h2>
            <p>Select match size, choose your color & custom pawn style!</p>
          </div>

          <!-- 1. Match Size Selector (2P, 3P, 4P, 5P, 6P) -->
          <div class="ludo-setup-section">
            <div class="ludo-section-label">👥 1. Board Match Size (Locked Once Started)</div>
            <div class="ludo-size-btn-group">
              <button class="btn-ludo-size ${this.matchSize === '2P' ? 'active' : ''} ${!isHost ? 'disabled' : ''}" data-size="2P">2P Duel</button>
              <button class="btn-ludo-size ${this.matchSize === '3P' ? 'active' : ''} ${!isHost ? 'disabled' : ''}" data-size="3P">3P Trio</button>
              <button class="btn-ludo-size ${this.matchSize === '4P' ? 'active' : ''} ${!isHost ? 'disabled' : ''}" data-size="4P">4P Classic</button>
              <button class="btn-ludo-size ${this.matchSize === '5P' ? 'active' : ''} ${!isHost ? 'disabled' : ''}" data-size="5P">5P Board</button>
              <button class="btn-ludo-size ${this.matchSize === '6P' ? 'active' : ''} ${!isHost ? 'disabled' : ''}" data-size="6P">6P Board</button>
            </div>
          </div>

          <!-- 2. Color Selection -->
          <div class="ludo-setup-section">
            <div class="ludo-section-label">🎨 2. Choose Your Token Color</div>
            <div class="ludo-color-chips">
              ${currentBoard.colors.map(col => {
                const isSelected = this.selectedColor === col;
                return `
                  <div class="ludo-color-chip ${isSelected ? 'selected' : ''}" 
                       data-color="${col}" 
                       style="background: ${this.colorHexes[col]}; color: ${this.colorHexes[col]};" 
                       title="${col.toUpperCase()}">
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- 3. Multiple Pawn Styles -->
          <div class="ludo-setup-section">
            <div class="ludo-section-label">🧸 3. Choose Your Pawn Style</div>
            <div class="ludo-pawn-styles">
              ${this.pawnStyles.map(style => {
                const isSelected = this.selectedPawnStyle === style.id;
                return `
                  <div class="ludo-pawn-card ${isSelected ? 'selected' : ''}" data-style="${style.id}">
                    <canvas class="ludo-pawn-preview" id="preview-${style.id}" width="32" height="32"></canvas>
                    <div class="ludo-pawn-name">${style.name.split(' ')[1]}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <!-- 4. Players Roster -->
          <div class="ludo-roster-box">
            <div class="ludo-section-label" style="font-size:0.68rem;">📋 Players in Room:</div>
            ${(this.roomPlayers.length > 0 ? this.roomPlayers : [{ name: 'You', id: this.socket.id }]).map(p => {
              const custom = this.customizations[p.id] || {};
              const pColor = custom.color || (p.id === this.socket.id ? this.selectedColor : currentBoard.colors[0]);
              const pStyle = custom.pawnStyle || (p.id === this.socket.id ? this.selectedPawnStyle : 'toy');
              return `
                <div class="ludo-roster-item">
                  <span style="display:flex; align-items:center; gap:6px;">
                    <span style="width:10px; height:10px; border-radius:50%; background:${this.colorHexes[pColor] || '#38bdf8'};"></span>
                    <strong>${p.name || 'Player'}</strong> ${p.id === this.hostId ? '👑' : ''}
                  </span>
                  <span style="color:#94a3b8; font-size:0.7rem;">Style: ${pStyle.toUpperCase()}</span>
                </div>
              `;
            }).join('')}
          </div>

          <!-- 5. Action Button -->
          ${isHost ? `
            <button class="btn-ludo-start" id="btn-ludo-enter">🚀 ENTER & START MATCH</button>
          ` : `
            <div class="ludo-waiting-host">⏳ Waiting for Room Host 👑 to enter game...</div>
          `}
        </div>
      </div>
    `;

    this.pawnStyles.forEach(st => {
      const cvs = document.getElementById(`preview-${st.id}`);
      if (cvs) {
        const ctx = cvs.getContext('2d');
        ctx.clearRect(0, 0, 32, 32);
        this.drawPawn(ctx, 16, 18, this.colorHexes[this.selectedColor] || '#ef4444', st.id, false, 0.7);
      }
    });

    if (isHost) {
      this.container.querySelectorAll('.btn-ludo-size').forEach(btn => {
        btn.addEventListener('click', () => {
          const size = btn.dataset.size;
          sounds.playPop();
          this.matchSize = size;
          this.boardConfig = LUDO_BOARDS[size];
          if (!this.boardConfig.colors.includes(this.selectedColor)) {
            this.selectedColor = this.boardConfig.colors[0];
          }
          this.socket.emit('ludo_setMatchSize', { matchSize: size });
          this.renderSetupScreen();
        });
      });

      document.getElementById('btn-ludo-enter')?.addEventListener('click', () => {
        sounds.playSuccess();
        this.socket.emit('ludo_enterGame');
      });
    }

    this.container.querySelectorAll('.ludo-color-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const color = chip.dataset.color;
        sounds.playPop();
        this.selectedColor = color;
        this.socket.emit('ludo_customize', { color: this.selectedColor, pawnStyle: this.selectedPawnStyle });
        this.renderSetupScreen();
      });
    });

    this.container.querySelectorAll('.ludo-pawn-card').forEach(card => {
      card.addEventListener('click', () => {
        const style = card.dataset.style;
        sounds.playPop();
        this.selectedPawnStyle = style;
        this.socket.emit('ludo_customize', { color: this.selectedColor, pawnStyle: this.selectedPawnStyle });
        this.renderSetupScreen();
      });
    });
  }

  // ========================================================
  // 4. ACTIVE IN-GAME SCREEN (WITH LEAVE OPTION & LEADERBOARD)
  // ========================================================
  renderGameScreen() {
    this.container.innerHTML = `
      <div class="ludo-wrapper">
        <div class="ludo-header">
          <div class="ludo-meta">
            <h2>🎲 LUDO (${this.boardConfig.name.toUpperCase()})</h2>
            <div id="ludo-turn-badge" class="ludo-badge">Waiting for turn...</div>
          </div>
          <div style="display:flex; align-items:center; gap:8px;">
            <div style="font-size:0.72rem; color:#94a3b8; font-weight:800;">🔒 MATCH LOCKED: ${this.matchSize}</div>
            <button id="btn-ludo-leave-game" class="btn-ludo-leave" title="Leave Match">🚪 LEAVE</button>
          </div>
        </div>

        <div class="ludo-main-area">
          <div class="ludo-board-box" style="position:relative;">
            <div id="ludo-banner-alert" class="ludo-banner-alert" style="display:none;"></div>
            <canvas id="ludo-canvas" width="600" height="600"></canvas>
          </div>

          <!-- Controls & 3D Rolling Dice -->
          <div class="ludo-controls-panel">
            <div class="dice-box" id="ludo-dice-box">
              <div class="dice-scene" id="ludo-dice-scene" title="Tap to Roll!">
                <div class="dice-cube" id="ludo-dice-cube">
                  <div class="dice-face face-front"><span class="pip pip-center pip-red"></span></div>
                  <div class="dice-face face-back">
                    <span class="pip"></span><span class="pip"></span>
                    <span class="pip"></span><span class="pip"></span>
                    <span class="pip"></span><span class="pip"></span>
                  </div>
                  <div class="dice-face face-right"><span class="pip"></span><span class="pip"></span></div>
                  <div class="dice-face face-left">
                    <span class="pip pip-red"></span><span class="pip pip-red"></span>
                    <span class="pip pip-red"></span>
                    <span class="pip pip-red"></span>
                    <span class="pip pip-red"></span>
                  </div>
                  <div class="dice-face face-top"><span class="pip"></span><span class="pip"></span><span class="pip"></span></div>
                  <div class="dice-face face-bottom">
                    <span class="pip pip-red"></span><span class="pip pip-red"></span>
                    <span class="pip pip-red"></span>
                    <span class="pip pip-red"></span>
                  </div>
                </div>
                <div class="dice-shadow-disc"></div>
              </div>

              <button id="btn-roll-dice" class="btn-roll-dice">🎲 ROLL DICE</button>
            </div>

            <!-- Players Token Summary -->
            <div class="ludo-players-list" id="ludo-players-list"></div>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('ludo-canvas');
    this.ctx = this.canvas.getContext('2d');

    const triggerRoll = () => {
      if (this.currentTurnId === this.socket.id && !this.isRolling) {
        sounds.playPop();
        this.socket.emit('ludo_roll');
      }
    };

    document.getElementById('btn-roll-dice')?.addEventListener('click', triggerRoll);
    document.getElementById('ludo-dice-scene')?.addEventListener('click', triggerRoll);

    document.getElementById('btn-ludo-leave-game')?.addEventListener('click', () => {
      sounds.playPop();
      this.socket.emit('ludo_leaveMatch');
      this.socket.emit('returnToLobby');
    });

    this.setupCanvasClicks();
    this.updateTurnUI();
  }

  showBanner(msg, duration = 2400) {
    const el = document.getElementById('ludo-banner-alert');
    if (!el) return;
    el.innerText = msg;
    el.style.display = 'block';
    if (this.bannerTimer) clearTimeout(this.bannerTimer);
    this.bannerTimer = setTimeout(() => {
      el.style.display = 'none';
    }, duration);
  }

  getOrdinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  showLeaderboardModal(leaderboard = []) {
    const existing = this.container.querySelector('.ludo-leaderboard-modal');
    if (existing) existing.remove();

    const medals = ['🥇', '🥈', '🥉', '4th', '5th', '6th'];
    const modal = document.createElement('div');
    modal.className = 'ludo-leaderboard-modal';
    modal.innerHTML = `
      <div class="ludo-leaderboard-card">
        <h2>🏆 MATCH COMPLETE</h2>
        <p class="ludo-subhead">Final Rankings & Battle Statistics</p>
        
        <div class="ludo-rankings-table">
          ${leaderboard.map((p, idx) => {
            const rank = p.rank || (idx + 1);
            const medal = medals[rank - 1] || `${rank}th`;
            const colHex = this.colorHexes[p.color] || '#38bdf8';
            return `
              <div class="ludo-rank-row rank-${rank}" style="border-left-color:${colHex};">
                <div class="ludo-rank-left">
                  <span class="ludo-rank-medal">${medal}</span>
                  <span style="width:10px; height:10px; border-radius:50%; background:${colHex};"></span>
                  <span>${p.name} ${p.hasLeft ? '(Left)' : ''}</span>
                </div>
                <div class="ludo-rank-stats">
                  <span class="ludo-stat-tag" title="Opponents Knocked Out">⚔️ ${p.kills || 0}</span>
                  <span class="ludo-stat-tag" title="Times Knocked Out">☠️ ${p.deaths || 0}</span>
                  <span class="ludo-stat-tag" title="Pawns Home">🏁 ${p.tokensHome ?? 4}/4</span>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <div class="ludo-leaderboard-actions">
          <button id="btn-lb-lobby" class="btn-lb-primary">🏠 RETURN TO ROOM LOBBY</button>
          <button id="btn-lb-hub" class="btn-lb-secondary">🌐 RETURN TO MAIN HUB</button>
        </div>
      </div>
    `;

    this.container.appendChild(modal);

    modal.querySelector('#btn-lb-lobby')?.addEventListener('click', () => {
      sounds.playPop();
      this.socket.emit('returnToLobby');
    });

    modal.querySelector('#btn-lb-hub')?.addEventListener('click', () => {
      sounds.playPop();
      window.location.reload();
    });
  }

  setupCanvasClicks() {
    const handleAction = (clientX, clientY) => {
      if (this.isRolling || this.currentTurnId !== this.socket.id || this.movableTokens.length === 0) return;

      const rect = this.canvas.getBoundingClientRect();
      const clickX = (clientX - rect.left) * (this.canvas.width / rect.width);
      const clickY = (clientY - rect.top) * (this.canvas.height / rect.height);

      const pState = this.players[this.socket.id];
      if (!pState) return;

      for (const tokenIdx of this.movableTokens) {
        const pos = pState.tokens[tokenIdx];
        const coord = this.getTokenBasePixelCoord(pState.color, tokenIdx, pos);
        if (Math.hypot(clickX - coord.x, clickY - coord.y) < 38) {
          sounds.playPop();
          this.socket.emit('ludo_move', { tokenIndex: tokenIdx });
          this.movableTokens = [];
          break;
        }
      }
    };

    this.canvas.addEventListener('click', (e) => handleAction(e.clientX, e.clientY));
    this.canvas.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches[0]) {
        e.preventDefault();
        handleAction(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });
  }

  // ========================================================
  // 5. SOCKET EVENTS
  // ========================================================
  setupSocketEvents() {
    this.socket.on('ludo_lobbySync', (data) => {
      this.matchSize = data.matchSize || this.matchSize;
      this.boardConfig = LUDO_BOARDS[this.matchSize] || this.boardConfig;
      this.customizations = data.customizations || {};
      this.roomPlayers = data.players || [];
      this.hostId = data.hostId;

      if (this.gameState === 'LOBBY') {
        this.renderSetupScreen();
      }
    });

    this.socket.on('ludo_started', (data) => {
      this.gameState = 'IN_GAME'; // LOCKED!
      this.matchSize = data.matchSize || this.matchSize;
      this.boardConfig = LUDO_BOARDS[this.matchSize] || LUDO_BOARDS['4P'];
      this.players = data.players;
      this.currentTurnId = data.currentTurnId;
      this.currentDice = data.currentDice;
      this.activeHops = [];
      this.movableTokens = [];
      this.pendingMovableTokens = [];

      this.renderGameScreen();
    });

    this.socket.on('ludo_diceRolled', (data) => {
      this.currentDice = data.dice;

      // RULE: Do NOT highlight movable tokens while die is rolling!
      this.movableTokens = [];
      this.pendingMovableTokens = data.playerId === this.socket.id ? (data.movableTokens || []) : [];

      if (data.forfeited) {
        this.showBanner(data.notice || '⚠️ 3 consecutive 6s! Turn forfeited!', 2500);
        sounds.playExplosion();
      }

      this.animate3DDiceRoll(data.dice, () => {
        if (!data.forfeited) {
          this.movableTokens = this.pendingMovableTokens;
          this.pendingMovableTokens = [];
          this.updateTurnUI();
        }
      });
    });

    this.socket.on('ludo_tokenMoved', (data) => {
      this.movableTokens = [];
      this.queueHopAnimation(data);
    });

    this.socket.on('ludo_playerFinished', (data) => {
      sounds.playSuccess();
      const medal = data.rank === 1 ? '🥇' : (data.rank === 2 ? '🥈' : (data.rank === 3 ? '🥉' : '🏁'));
      this.showBanner(`${medal} ${data.name} completed in ${this.getOrdinal(data.rank)} Place!`, 3000);
      this.updateTurnUI();
    });

    this.socket.on('ludo_playerLeft', (data) => {
      this.showBanner(`🚪 ${data.name} left the match.`, 2500);
      this.updateTurnUI();
    });

    this.socket.on('ludo_turnChange', (data) => {
      this.currentTurnId = data.currentTurnId;
      this.movableTokens = [];
      this.pendingMovableTokens = [];
      this.updateTurnUI();
    });

    this.socket.on('ludo_extraTurn', (data) => {
      sounds.playSuccess();
      if (data.reason) {
        this.showBanner(data.reason, 2200);
      }
      this.updateTurnUI();
    });

    this.socket.on('ludo_gameOver', (data) => {
      sounds.playVictory();
      this.showLeaderboardModal(data.leaderboard || []);
    });
  }

  // ========================================================
  // 6. 3D DICE & STEP HOPPING ANIMATIONS
  // ========================================================
  animate3DDiceRoll(targetDice, onComplete) {
    const cube = document.getElementById('ludo-dice-cube');
    if (!cube) {
      if (onComplete) onComplete();
      return;
    }

    this.isRolling = true;
    cube.classList.add('rolling');
    sounds.playHover();

    const tickInterval = setInterval(() => {
      sounds.playTick();
    }, 110);

    setTimeout(() => {
      clearInterval(tickInterval);
      cube.classList.remove('rolling');

      const faceRotations = {
        1: { x: 720, y: 720 },
        2: { x: 720, y: 630 },
        3: { x: 630, y: 720 },
        4: { x: 810, y: 720 },
        5: { x: 720, y: 810 },
        6: { x: 720, y: 900 }
      };

      const rot = faceRotations[targetDice] || { x: 720, y: 720 };
      cube.style.transform = `rotateX(${rot.x}deg) rotateY(${rot.y}deg)`;

      sounds.playPop();
      this.isRolling = false;

      if (onComplete) onComplete();
    }, 700);
  }

  queueHopAnimation(data) {
    const { playerId, tokenIndex, oldPos, newPos, players, knocked, reachedHome } = data;
    const player = this.players[playerId];
    if (!player) {
      this.players = players;
      return;
    }

    const steps = [];
    if (oldPos === -1 && newPos === 0) {
      steps.push({ pos: 0, arc: 20 });
    } else {
      for (let p = oldPos + 1; p <= newPos; p++) {
        steps.push({ pos: p, arc: 14 });
      }
    }

    this.activeHops.push({
      playerId,
      tokenIndex,
      color: player.color,
      steps,
      currentStepIdx: 0,
      stepStartTime: performance.now(),
      stepDuration: 130,
      finalPlayers: players,
      knocked,
      reachedHome
    });
  }

  startAnimationLoop() {
    const loop = (now) => {
      this.updateAnimations(now);
      if (this.gameState === 'IN_GAME') {
        this.drawBoard();
      }
      this.animFrameId = requestAnimationFrame(loop);
    };
    this.animFrameId = requestAnimationFrame(loop);
  }

  updateAnimations(now) {
    if (this.activeHops.length === 0) return;

    for (let i = this.activeHops.length - 1; i >= 0; i--) {
      const hop = this.activeHops[i];
      const elapsed = now - hop.stepStartTime;
      const progress = Math.min(1, elapsed / hop.stepDuration);

      if (progress >= 1) {
        hop.currentStepIdx++;
        if (hop.currentStepIdx >= hop.steps.length) {
          this.players = hop.finalPlayers;
          this.activeHops.splice(i, 1);

          sounds.playChime();
          if (hop.knocked) {
            sounds.playExplosion();
            this.showBanner(`💥 Captured ${hop.knocked.name}'s pawn!`, 2000);
          }
          if (hop.reachedHome) {
            sounds.playSuccess();
            this.showBanner(`🏁 Pawn reached HOME GOAL!`, 2000);
          }
          this.updateTurnUI();
        } else {
          hop.stepStartTime = now;
          sounds.playTick();
        }
      }
    }
  }

  getActiveHopState(playerId, tokenIndex) {
    const hop = this.activeHops.find(h => h.playerId === playerId && h.tokenIndex === tokenIndex);
    if (!hop) return null;

    const step = hop.steps[hop.currentStepIdx];
    if (!step) return null;

    const elapsed = performance.now() - hop.stepStartTime;
    const progress = Math.min(1, elapsed / hop.stepDuration);
    const hopArc = Math.sin(progress * Math.PI) * step.arc;

    return { pos: step.pos, hopArc };
  }

  updateTurnUI() {
    const badge = document.getElementById('ludo-turn-badge');
    const rollBtn = document.getElementById('btn-roll-dice');
    const isMe = this.currentTurnId === this.socket.id;
    const curP = this.players[this.currentTurnId];

    if (badge) {
      if (curP?.isFinished) {
        badge.innerText = `🏁 ${curP.name} Finished!`;
        badge.style.color = '#eab308';
      } else {
        badge.innerText = isMe ? '👉 YOUR TURN TO ROLL!' : `Turn: ${curP?.name || 'Player'}`;
        badge.style.color = isMe ? '#22c55e' : '#38bdf8';
      }
    }
    if (rollBtn) {
      if (isMe && !this.isRolling && this.movableTokens.length === 0 && !curP?.isFinished) {
        rollBtn.classList.remove('disabled');
      } else {
        rollBtn.classList.add('disabled');
      }
    }

    const pList = document.getElementById('ludo-players-list');
    if (pList) {
      const homeGoal = this.boardConfig.homePos;
      pList.innerHTML = Object.values(this.players).map(p => `
        <div class="ludo-p-row ${p.id === this.currentTurnId ? 'active' : ''}" style="border-left: 4px solid ${this.colorHexes[p.color] || '#38bdf8'};">
          <span>${p.name} (${p.pawnStyle || 'toy'}) ${p.isFinished ? '🏆' : ''}</span>
          <span>🏁 ${p.tokens.filter(t => t === homeGoal).length}/4</span>
        </div>
      `).join('');
    }
  }

  // ========================================================
  // 7. BOARD RENDERING
  // ========================================================
  drawBoard() {
    if (!this.ctx) return;
    const ctx = this.ctx;

    ctx.fillStyle = '#0b0f19';
    ctx.fillRect(0, 0, 600, 600);

    if (this.matchSize === '6P') {
      this.drawReferenceBoard(ctx, this.coords6P);
    } else if (this.matchSize === '5P') {
      this.drawReferenceBoard(ctx, this.coords5P);
    } else {
      this.drawClassic15Board(ctx);
    }

    this.drawTokensClustered(ctx);
  }

  // --------------------------------------------------------
  // Classic 15x15 Board (Preserved for 2P, 3P, 4P)
  // --------------------------------------------------------
  drawClassic15Board(ctx) {
    const cs = 40;
    const activeColors = this.boardConfig.colors;

    // Corner Bases (6x6 cells each)
    this.drawSquareBase(ctx, 0, 0, this.colorHexes.red, this.coords15.bases.red, activeColors.includes('red'));
    this.drawSquareBase(ctx, 9 * cs, 0, this.colorHexes.green, this.coords15.bases.green, activeColors.includes('green'));
    this.drawSquareBase(ctx, 9 * cs, 9 * cs, this.colorHexes.yellow, this.coords15.bases.yellow, activeColors.includes('yellow'));
    this.drawSquareBase(ctx, 0, 9 * cs, this.colorHexes.blue, this.coords15.bases.blue, activeColors.includes('blue'));

    // 15x15 Track Paths
    for (let r = 0; r < 15; r++) {
      for (let c = 0; c < 15; c++) {
        if ((c < 6 && r < 6) || (c > 8 && r < 6) || (c < 6 && r > 8) || (c > 8 && r > 8)) continue;
        if (c >= 6 && c <= 8 && r >= 6 && r <= 8) continue;

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.14)';
        ctx.lineWidth = 1;
        ctx.strokeRect(c * cs, r * cs, cs, cs);

        // Home Stretches
        if (r === 7 && c >= 1 && c <= 5 && activeColors.includes('red')) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.red);
        if (c === 7 && r >= 1 && r <= 5 && activeColors.includes('green')) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.green);
        if (r === 7 && c >= 9 && c <= 13 && activeColors.includes('yellow')) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.yellow);
        if (c === 7 && r >= 9 && r <= 13 && activeColors.includes('blue')) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.blue);

        // Start Squares with ★
        if (c === 1 && r === 6) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.red, '★');
        if (c === 8 && r === 1) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.green, '★');
        if (c === 13 && r === 8) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.yellow, '★');
        if (c === 6 && r === 13) this.fillCell(ctx, c * cs, r * cs, cs, this.colorHexes.blue, '★');

        // Safe Stars
        if ((c === 6 && r === 2) || (c === 12 && r === 6) || (c === 8 && r === 12) || (c === 2 && r === 8)) {
          ctx.fillStyle = '#fbbf24';
          ctx.font = '16px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('☆', c * cs + 20, r * cs + 26);
        }
      }
    }

    // Center Home Triangles
    const cx = 300, cy = 300;
    this.drawTriangle(ctx, cx, cy, 240, 240, 240, 360, this.colorHexes.red);
    this.drawTriangle(ctx, cx, cy, 240, 240, 360, 240, this.colorHexes.green);
    this.drawTriangle(ctx, cx, cy, 360, 240, 360, 360, this.colorHexes.yellow);
    this.drawTriangle(ctx, cx, cy, 240, 360, 360, 360, this.colorHexes.blue);

    // Dedicated Home Reached Token Pedestals in center triangles
    const dedicated4PSlots = {
      red: [{ x: 260, y: 285 }, { x: 260, y: 315 }, { x: 278, y: 290 }, { x: 278, y: 310 }],
      green: [{ x: 285, y: 260 }, { x: 315, y: 260 }, { x: 290, y: 278 }, { x: 310, y: 278 }],
      yellow: [{ x: 340, y: 285 }, { x: 340, y: 315 }, { x: 322, y: 290 }, { x: 322, y: 310 }],
      blue: [{ x: 285, y: 340 }, { x: 315, y: 340 }, { x: 290, y: 322 }, { x: 310, y: 322 }]
    };
    activeColors.forEach(col => {
      dedicated4PSlots[col]?.forEach(pt => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.22)';
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, 7, 0, Math.PI * 2);
        ctx.fill();
      });
    });
  }

  drawSquareBase(ctx, bx, by, color, pedestals, isActive = true) {
    const cs = 40;
    ctx.fillStyle = isActive ? color : 'rgba(30, 41, 59, 0.6)';
    ctx.fillRect(bx, by, 6 * cs, 6 * cs);

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(bx + cs, by + cs, 4 * cs, 4 * cs);

    pedestals.forEach((p) => {
      ctx.fillStyle = isActive ? color : 'rgba(255, 255, 255, 0.15)';
      ctx.beginPath();
      ctx.arc(p.c * cs + 20, p.r * cs + 20, 16, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // --------------------------------------------------------
  // EXACT BOARD FORMAT FOR 5P & 6P:
  // - 6 Colored Wedges with White Circular Bases & 4 Token Holes
  // - Outer Tabs Labeled Player 1..6
  // - 3x6 Arms: Line 1 (START square at Row 4 with ★),
  //   Line 2 (5 colored home squares + white tip with arrow),
  //   Line 3 (Row 3 is the ONLY safe star ☆)
  // - DEDICATED COLOR HOME TRAPEZOIDS (All 4 reached tokens 100% visible!)
  // --------------------------------------------------------
  drawReferenceBoard(ctx, geom) {
    const cx = 300, cy = 300;
    const { armsCount, colors } = geom;
    const stepAngle = (2 * Math.PI) / armsCount;
    const startAngleOffset = -Math.PI / 2; // Arm 0 points Up (12 o'clock, Red)

    // 1. Board Outer Background Disc
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, cy, 292, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 3;
    ctx.stroke();

    // 2. Symmetrical Colored Triangular Wedges with White Circular Bases
    colors.forEach((color, armIdx) => {
      const armAngle = startAngleOffset + armIdx * stepAngle;
      const baseAngle = armAngle - stepAngle / 2;
      const colHex = this.colorHexes[color];

      // Draw Colored Wedge Polygon
      const a1 = armAngle - stepAngle;
      const a2 = armAngle;
      const rOuter = 292;
      const rInner = 64;

      ctx.fillStyle = colHex;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(a1) * rInner, cy + Math.sin(a1) * rInner);
      ctx.lineTo(cx + Math.cos(a1) * rOuter, cy + Math.sin(a1) * rOuter);
      ctx.arc(cx, cy, rOuter, a1, a2);
      ctx.lineTo(cx + Math.cos(a2) * rInner, cy + Math.sin(a2) * rInner);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Outer Player Label Tab
      const tabR = 278;
      const tabX = cx + Math.cos(baseAngle) * tabR;
      const tabY = cy + Math.sin(baseAngle) * tabR;
      ctx.save();
      ctx.translate(tabX, tabY);
      ctx.rotate(baseAngle + Math.PI / 2);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 9px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`Player ${armIdx + 1}`, 0, 0);
      ctx.restore();

      // Large White Circular Base Disc
      const bx = cx + Math.cos(baseAngle) * 206;
      const by = cy + Math.sin(baseAngle) * 206;
      const baseRadius = 45;

      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(bx, by, baseRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // 4 Circular Token Holes in Player Color
      const d = 16;
      const holes = [
        { x: bx - d, y: by - d },
        { x: bx + d, y: by - d },
        { x: bx - d, y: by + d },
        { x: bx + d, y: by + d }
      ];
      holes.forEach(h => {
        ctx.fillStyle = colHex;
        ctx.beginPath();
        ctx.arc(h.x, h.y, 11, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      });
    });

    // 3. Rectangular 3x6 Track Arms
    // 3. Rectangular 3x6 Track Arms
    colors.forEach((color, armIdx) => {
      const armAngle = startAngleOffset + armIdx * stepAngle;
      const nextPlayerColor = colors[(armIdx + 1) % armsCount];
      const runwayHex = this.colorHexes[nextPlayerColor];
      const cols = [-22, 0, 22]; // Line 1 (Left), Line 2 (Middle Runway), Line 3 (Right)
      const rows = [72, 104, 136, 168, 200, 232]; // Rows 0 to 5

      // Draw Cells
      rows.forEach((radial, rIdx) => {
        cols.forEach((lateral, cIdx) => {
          const pTL = LudoGameClient.getArmPoint(armAngle, lateral - 11, radial - 16, cx, cy);
          const pTR = LudoGameClient.getArmPoint(armAngle, lateral + 11, radial - 16, cx, cy);
          const pBR = LudoGameClient.getArmPoint(armAngle, lateral + 11, radial + 16, cx, cy);
          const pBL = LudoGameClient.getArmPoint(armAngle, lateral - 11, radial + 16, cx, cy);
          const center = LudoGameClient.getArmPoint(armAngle, lateral, radial, cx, cy);

          // Line 2 (Middle): Home Runway for next player (e.g. Red's home path between Purple & Red)
          const isHomeRunway = (cIdx === 1 && rIdx <= 4);

          // Line 3 Row 4 is next player's START SQUARE (touching next lobby)
          const isStartSquare = (cIdx === 2 && rIdx === 4);

          // Line 1 Row 3 is the SAFE STAR POINT (☆)
          const isSafeStar = (cIdx === 0 && rIdx === 3);

          ctx.fillStyle = isHomeRunway ? runwayHex : (isStartSquare ? runwayHex : '#ffffff');
          ctx.beginPath();
          ctx.moveTo(pTL.x, pTL.y);
          ctx.lineTo(pTR.x, pTR.y);
          ctx.lineTo(pBR.x, pBR.y);
          ctx.lineTo(pBL.x, pBL.y);
          ctx.closePath();
          ctx.fill();

          ctx.strokeStyle = '#1e293b';
          ctx.lineWidth = 1.2;
          ctx.stroke();

          // START SQUARE: Safe Star Entry Point with bold white star ★
          if (isStartSquare) {
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 15px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('★', center.x, center.y);
          }

          // Middle Line Row 5: Outer tip square with inward arrow pointing to 5 home runway squares
          if (cIdx === 1 && rIdx === 5) {
            ctx.save();
            ctx.translate(center.x, center.y);
            ctx.rotate(armAngle);
            ctx.fillStyle = runwayHex;
            ctx.beginPath();
            ctx.moveTo(0, 7);
            ctx.lineTo(-6, -5);
            ctx.lineTo(6, -5);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          }

          // EXACTLY 1 SAFE STAR POINT (☆) - Clean outline star!
          if (isSafeStar) {
            ctx.fillStyle = '#1e293b';
            ctx.font = 'bold 18px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('☆', center.x, center.y);
          }
        });
      });

      // Outer Arm Border
      const armTL = LudoGameClient.getArmPoint(armAngle, -33, 56, cx, cy);
      const armTR = LudoGameClient.getArmPoint(armAngle, 33, 56, cx, cy);
      const armBR = LudoGameClient.getArmPoint(armAngle, 33, 248, cx, cy);
      const armBL = LudoGameClient.getArmPoint(armAngle, -33, 248, cx, cy);

      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(armTL.x, armTL.y);
      ctx.lineTo(armTR.x, armTR.y);
      ctx.lineTo(armBR.x, armBR.y);
      ctx.lineTo(armBL.x, armBL.y);
      ctx.closePath();
      ctx.stroke();
    });

    // 4. Central Color Home Triangles (DEDICATED COLOR HOME SECTIONS - NO DICE SYMBOL)
    const centerOuterR = 64;

    colors.forEach((color, pIdx) => {
      // Home triangle is at the base of prevArm (matching that player's home runway)
      const prevArm = (pIdx - 1 + armsCount) % armsCount;
      const prevArmAngle = startAngleOffset + prevArm * stepAngle;
      const a1 = prevArmAngle - stepAngle / 2;
      const a2 = prevArmAngle + stepAngle / 2;
      const colHex = this.colorHexes[color];

      // Draw this player's DEDICATED COLOR HOME TRIANGLE right into the exact center
      const p1Outer = { x: cx + Math.cos(a1) * centerOuterR, y: cy + Math.sin(a1) * centerOuterR };
      const p2Outer = { x: cx + Math.cos(a2) * centerOuterR, y: cy + Math.sin(a2) * centerOuterR };

      ctx.fillStyle = colHex;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(p1Outer.x, p1Outer.y);
      ctx.lineTo(p2Outer.x, p2Outer.y);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = '#1e293b';
      ctx.lineWidth = 2;
      ctx.stroke();

      // 4 Small Dedicated Home Pedestal Rings inside this color's triangle
      const slots = geom.dedicatedHomeSlots?.[color] || [];
      slots.forEach(slot => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.beginPath();
        ctx.arc(slot.x, slot.y, 6.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    });

    // 5. Clean Center Pivot Point (NO dice symbol in middle of homes)
    ctx.fillStyle = '#1e293b';
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  fillCell(ctx, x, y, cs, color, text = null) {
    ctx.fillStyle = color;
    ctx.fillRect(x + 2, y + 2, cs - 4, cs - 4);
    if (text) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(text, x + cs / 2, y + cs / 2 + 6);
    }
  }

  drawTriangle(ctx, x1, y1, x2, y2, x3, y3, color) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x3, y3);
    ctx.closePath();
    ctx.fill();
  }

  // ========================================================
  // 8. MULTI-PAWN CLUSTERING & CUSTOM 3D PAWN RENDERER
  // ========================================================
  drawTokensClustered(ctx) {
    const clusters = {};

    for (const id in this.players) {
      const p = this.players[id];
      p.tokens.forEach((pos, tokenIdx) => {
        const hopState = this.getActiveHopState(p.id, tokenIdx);
        const effectivePos = hopState ? hopState.pos : pos;

        const baseCoord = this.getTokenBasePixelCoord(p.color, tokenIdx, effectivePos);
        const locKey = baseCoord.key;

        if (!clusters[locKey]) clusters[locKey] = [];

        clusters[locKey].push({
          playerId: p.id,
          tokenIdx,
          color: p.color,
          pawnStyle: p.pawnStyle || 'toy',
          hopArc: hopState ? hopState.hopArc : 0,
          baseCoord,
          isMovable: this.movableTokens.includes(tokenIdx) && id === this.socket.id
        });
      });
    }

    // Draw clustered pawns with dynamic offsets so every pawn is visible
    for (const key in clusters) {
      const tokens = clusters[key];
      const count = tokens.length;
      const baseCoord = tokens[0].baseCoord;

      tokens.forEach((item, i) => {
        let offsetX = 0;
        let offsetY = 0;
        let scale = 1.0;

        if (count === 2) {
          scale = 0.82;
          offsetX = i === 0 ? -9 : 9;
          offsetY = i === 0 ? -6 : 6;
        } else if (count === 3) {
          scale = 0.74;
          const a = (i * 2 * Math.PI / 3) - Math.PI / 2;
          offsetX = Math.cos(a) * 11;
          offsetY = Math.sin(a) * 11;
        } else if (count >= 4) {
          scale = 0.66;
          const a = (i * 2 * Math.PI / count) - Math.PI / 2;
          offsetX = Math.cos(a) * 13;
          offsetY = Math.sin(a) * 13;
        }

        const x = baseCoord.x + offsetX;
        const y = baseCoord.y + offsetY - item.hopArc;

        this.drawPawn(ctx, x, y, this.colorHexes[item.color] || '#38bdf8', item.pawnStyle, item.isMovable, scale);
      });
    }
  }

  // --------------------------------------------------------
  // PAWN STYLES DISPATCHER: Toy, Crown, Rocket, Gem, Chibi
  // --------------------------------------------------------
  drawPawn(ctx, x, y, colorHex, style = 'toy', isMovable = false, scale = 1.0) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);

    // Ground Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.beginPath();
    ctx.ellipse(2, 10, 12, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // Movable Golden Aura
    if (isMovable) {
      const pulse = Math.sin(performance.now() / 140) * 3;
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 3.5;
      ctx.shadowColor = '#fbbf24';
      ctx.shadowBlur = 14 + pulse;
      ctx.beginPath();
      ctx.arc(0, 2, 19 + pulse * 0.5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    if (style === 'crown') {
      this.drawCrownPawn(ctx, colorHex);
    } else if (style === 'rocket') {
      this.drawRocketPawn(ctx, colorHex);
    } else if (style === 'gem') {
      this.drawGemPawn(ctx, colorHex);
    } else if (style === 'chibi') {
      this.drawChibiPawn(ctx, colorHex);
    } else {
      this.drawToyPawn(ctx, colorHex);
    }

    ctx.restore();
  }

  // 1. 🧸 Classic 3D Toy Pawn
  drawToyPawn(ctx, colorHex) {
    const baseGrad = ctx.createRadialGradient(-3, 6, 2, 0, 8, 12);
    baseGrad.addColorStop(0, '#ffffff');
    baseGrad.addColorStop(0.35, colorHex);
    baseGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = baseGrad;
    ctx.beginPath();
    ctx.ellipse(0, 8, 11, 4.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.stroke();

    const bodyGrad = ctx.createLinearGradient(-10, 0, 10, 0);
    bodyGrad.addColorStop(0, '#ffffff');
    bodyGrad.addColorStop(0.25, colorHex);
    bodyGrad.addColorStop(0.85, colorHex);
    bodyGrad.addColorStop(1, '#1e1b4b');
    ctx.fillStyle = bodyGrad;
    ctx.beginPath();
    ctx.moveTo(-10, 7);
    ctx.bezierCurveTo(-9, 2, -4, -2, -4, -6);
    ctx.lineTo(4, -6);
    ctx.bezierCurveTo(4, -2, 9, 2, 10, 7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.ellipse(0, -6, 5.5, 2, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    const headGrad = ctx.createRadialGradient(-2.5, -15, 1, 0, -13, 7.5);
    headGrad.addColorStop(0, '#ffffff');
    headGrad.addColorStop(0.3, colorHex);
    headGrad.addColorStop(0.9, colorHex);
    headGrad.addColorStop(1, '#0f172a');
    ctx.fillStyle = headGrad;
    ctx.beginPath();
    ctx.arc(0, -13, 7, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.beginPath();
    ctx.arc(-2.5, -15.5, 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // 2. 👑 Royal Crown Pawn
  drawCrownPawn(ctx, colorHex) {
    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.ellipse(0, 8, 12, 5, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = colorHex;
    ctx.beginPath();
    ctx.moveTo(-8, 7);
    ctx.lineTo(-4, -4);
    ctx.lineTo(4, -4);
    ctx.lineTo(8, 7);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#fbbf24';
    ctx.stroke();

    ctx.fillStyle = '#fbbf24';
    ctx.beginPath();
    ctx.moveTo(-8, -4);
    ctx.lineTo(-9, -15);
    ctx.lineTo(-4, -9);
    ctx.lineTo(0, -17);
    ctx.lineTo(4, -9);
    ctx.lineTo(9, -15);
    ctx.lineTo(8, -4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = colorHex;
    ctx.beginPath();
    ctx.arc(-9, -15, 2, 0, Math.PI * 2);
    ctx.arc(0, -17, 2.5, 0, Math.PI * 2);
    ctx.arc(9, -15, 2, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. 🚀 Cyber Rocket Pawn
  drawRocketPawn(ctx, colorHex) {
    ctx.fillStyle = '#38bdf8';
    ctx.beginPath();
    ctx.ellipse(0, 8, 6, 3, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.moveTo(-11, 8);
    ctx.lineTo(-5, 0);
    ctx.lineTo(-5, 7);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(11, 8);
    ctx.lineTo(5, 0);
    ctx.lineTo(5, 7);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = colorHex;
    ctx.beginPath();
    ctx.moveTo(-6, 7);
    ctx.lineTo(-6, -4);
    ctx.bezierCurveTo(-6, -16, 0, -20, 0, -20);
    ctx.bezierCurveTo(0, -20, 6, -16, 6, -4);
    ctx.lineTo(6, 7);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(0, -8, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // 4. 💎 Crystal Gem Pawn
  drawGemPawn(ctx, colorHex) {
    ctx.fillStyle = colorHex;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1.2;

    ctx.beginPath();
    ctx.moveTo(-7, -14);
    ctx.lineTo(7, -14);
    ctx.lineTo(11, -5);
    ctx.lineTo(0, 9);
    ctx.lineTo(-11, -5);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-7, -14);
    ctx.lineTo(0, -5);
    ctx.lineTo(7, -14);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-11, -5);
    ctx.lineTo(0, -5);
    ctx.lineTo(11, -5);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, -5);
    ctx.lineTo(0, 9);
    ctx.stroke();
  }

  // 5. 🐱 Chibi Animal Mascot Pawn
  drawChibiPawn(ctx, colorHex) {
    ctx.fillStyle = colorHex;
    ctx.beginPath();
    ctx.ellipse(0, 4, 10, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, -9, 8.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(-8, -13);
    ctx.lineTo(-5, -20);
    ctx.lineTo(-1, -16);
    ctx.closePath();
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(8, -13);
    ctx.lineTo(5, -20);
    ctx.lineTo(1, -16);
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.arc(-3, -9, 1.5, 0, Math.PI * 2);
    ctx.arc(3, -9, 1.5, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = '#f43f5e';
    ctx.beginPath();
    ctx.arc(-5, -6, 1.2, 0, Math.PI * 2);
    ctx.arc(5, -6, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // ========================================================
  // 9. PIXEL COORDINATES RETRIEVAL
  // ========================================================
  getTokenBasePixelCoord(color, tokenIdx, pos) {
    if (this.matchSize === '6P') {
      return this.getTokenCoordRadial(this.coords6P, color, tokenIdx, pos);
    } else if (this.matchSize === '5P') {
      return this.getTokenCoordRadial(this.coords5P, color, tokenIdx, pos);
    } else {
      return this.getTokenCoord15(color, tokenIdx, pos);
    }
  }

  getTokenCoord15(color, tokenIdx, pos) {
    const cellSize = 40;
    if (pos === -1) {
      const p = this.coords15.bases[color]?.[tokenIdx] || { c: 2, r: 2 };
      return { x: p.c * cellSize + 20, y: p.r * cellSize + 20, key: `base_${color}_${tokenIdx}` };
    }

    if (pos >= 0 && pos <= 50) {
      const boardIdx = (pos + this.boardConfig.startOffsets[color]) % 52;
      const c = this.coords15.path[boardIdx];
      return { x: c.c * cellSize + 20, y: c.r * cellSize + 20, key: `path_${boardIdx}` };
    }

    if (pos >= 51 && pos <= 55) {
      const stretchIdx = pos - 51;
      const c = this.coords15.homeStretches[color]?.[stretchIdx] || { c: 7, r: 7 };
      return { x: c.c * cellSize + 20, y: c.r * cellSize + 20, key: `stretch_${color}_${stretchIdx}` };
    }

    // 4P Dedicated Home Slots in each color's center triangle!
    const dedicated4PSlots = {
      red: [{ x: 260, y: 285 }, { x: 260, y: 315 }, { x: 278, y: 290 }, { x: 278, y: 310 }],
      green: [{ x: 285, y: 260 }, { x: 315, y: 260 }, { x: 290, y: 278 }, { x: 310, y: 278 }],
      yellow: [{ x: 340, y: 285 }, { x: 340, y: 315 }, { x: 322, y: 290 }, { x: 322, y: 310 }],
      blue: [{ x: 285, y: 340 }, { x: 315, y: 340 }, { x: 290, y: 322 }, { x: 310, y: 322 }]
    };

    const slot = dedicated4PSlots[color]?.[tokenIdx];
    if (slot) {
      return { x: slot.x, y: slot.y, key: `home_${color}_${tokenIdx}` };
    }

    return { x: this.coords15.homeCenter.x, y: this.coords15.homeCenter.y, key: `home_${color}_${tokenIdx}` };
  }

  getTokenCoordRadial(geom, color, tokenIdx, pos) {
    if (pos === -1) {
      const p = geom.bases[color]?.[tokenIdx] || { x: 300, y: 300 };
      return { x: p.x, y: p.y, key: `base_${color}_${tokenIdx}` };
    }

    const mainTrackLimit = this.boardConfig.pathLength - 1; // 77 for 6P, 64 for 5P
    if (pos >= 0 && pos <= mainTrackLimit) {
      const boardIdx = (pos + this.boardConfig.startOffsets[color]) % this.boardConfig.pathLength;
      const pt = geom.path[boardIdx] || { x: 300, y: 300 };
      return { x: pt.x, y: pt.y, key: `path_${boardIdx}` };
    }

    const stretchIdx = pos - (mainTrackLimit + 1); // 0 to 4
    const stretch = geom.homeStretches[color];
    if (stretch && stretch[stretchIdx]) {
      return { x: stretch[stretchIdx].x, y: stretch[stretchIdx].y, key: `stretch_${color}_${stretchIdx}` };
    }

    // DEDICATED COLOR HOME SLOTS: Each reached token gets its own distinct spot on that color's center section!
    const homeSlot = geom.dedicatedHomeSlots?.[color]?.[tokenIdx];
    if (homeSlot) {
      return { x: homeSlot.x, y: homeSlot.y, key: `home_${color}_${tokenIdx}` };
    }

    return { x: geom.homeCenter.x, y: geom.homeCenter.y, key: `home_${color}_${tokenIdx}` };
  }

  destroy() {
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.bannerTimer) {
      clearTimeout(this.bannerTimer);
      this.bannerTimer = null;
    }
    this.socket.off('ludo_lobbySync');
    this.socket.off('ludo_started');
    this.socket.off('ludo_diceRolled');
    this.socket.off('ludo_tokenMoved');
    this.socket.off('ludo_playerFinished');
    this.socket.off('ludo_playerLeft');
    this.socket.off('ludo_turnChange');
    this.socket.off('ludo_extraTurn');
    this.socket.off('ludo_gameOver');
  }
}
